import { keyframes } from "@emotion/react";
import tw from "twin.macro";

type Direction = "up" | "down";

type ProgressiveVerticalArrowProps = {
  /** Direction in which the arrow is drawn (default: "up"). */
  direction?: Direction;
  /** Stroke color (any valid CSS color, default: blue-500 @ 80%). */
  color?: string;
  /** Total duration of one animation cycle in seconds (default: 2). */
  durationSeconds?: number;
  className?: string;
};

/**
 * SVG arrow revealed progressively along the vertical axis, then faded out
 * and restarted in a loop.
 *
 * Uses a `clip-path` window that travels upward rather than `stroke-dashoffset`.
 * This guarantees:
 *   - a perfectly symmetric arrow head (both wings are drawn in full),
 *   - no "round linecap" ghost artifacts at the extremities during the loop
 *     reset.
 *
 * Pure & presentation-only: no state, no props mutation.
 */
const ProgressiveVerticalArrow = ({
  direction = "up",
  color = "rgb(59 130 246 / 0.8)",
  durationSeconds = 2,
  className,
}: ProgressiveVerticalArrowProps) => {
  return (
    <div
      className={className}
      css={[
        tw`w-full h-full flex items-center justify-center`,
        direction === "down" && tw`rotate-180`,
      ]}
    >
      <div
        aria-hidden="true"
        css={[
          tw`w-full h-full`,
          {
            clipPath: "inset(100% 0 0 0)",
            animation: `${rise} ${durationSeconds}s ease-in-out infinite`,
          },
        ]}
      >
        <svg
          viewBox="0 0 100 240"
          preserveAspectRatio="xMidYMid meet"
          tw="w-full h-full"
        >
          <g
            stroke={color}
            strokeWidth={14}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          >
            {/* Shaft — vertical line from bottom to the tip. */}
            <path d="M 50 210 L 50 40" />
            {/* Arrow head — symmetric chevron pointing up (flipped when direction="down"). */}
            <path d="M 22 65 L 50 37 L 78 65" />
          </g>
        </svg>
      </div>
    </div>
  );
};

/**
 * Reveals the clip window from the bottom up (0% → 50%), holds it fully
 * open (50% → 75%), fades out (75% → 95%), then snaps the clip closed
 * while still invisible (95% → 100%) so the next loop restarts cleanly.
 */
const rise = keyframes`
  0%   { clip-path: inset(100% 0 0 0); opacity: 1; }
  50%  { clip-path: inset(0   0 0 0); opacity: 1; }
  75%  { clip-path: inset(0   0 0 0); opacity: 1; }
  95%  { clip-path: inset(0   0 0 0); opacity: 0; }
  100% { clip-path: inset(100% 0 0 0); opacity: 0; }
`;

export default ProgressiveVerticalArrow;
